import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import useManageWebsites from './useManageWebsites';
import useGetCustomerProfile from '../../../hooks/useGetCustomerProfile';
import ProfileCombo from '../../../sharedComponents/ProfileCombo';
import WebsiteFeeExclusionService from './WebsiteFeeExclusionService';

const WebsiteRow = ({ website, profiles }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exclusions, setExclusions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comboKey, setComboKey] = useState(0); // reset ProfileCombo after add

  const toggle = async () => {
    if (!isOpen && !loaded) {
      setLoading(true);
      try {
        const res = await WebsiteFeeExclusionService.getExclusionsByWebsite(website.id);
        setExclusions(res.data);
        setLoaded(true);
      } catch {
        toast.error('โหลดรายการยกเว้นไม่สำเร็จ');
      } finally {
        setLoading(false);
      }
    }
    setIsOpen(prev => !prev);
  };

  // Filter out already-excluded customers so they don't appear in the combobox
  const excludedCustomerIds = new Set(exclusions.map(e => e.customer?.id));
  const availableProfiles = profiles.filter(p => !excludedCustomerIds.has(p.id));

  const handleSelect = async (smileName) => {
    const customer = profiles.find(p => p.smileName === smileName);
    if (!customer) return;
    if (excludedCustomerIds.has(customer.id)) return; // already excluded
    try {
      await WebsiteFeeExclusionService.addExclusion(website.id, customer.id);
      setExclusions(prev => [...prev, { id: Date.now(), customer: { id: customer.id, contact: customer.contact, smileName: customer.smileName } }]);
      setComboKey(k => k + 1); // reset the combobox
      toast.success(`เพิ่ม ${customer.contact || customer.smileName} สำเร็จ`);
    } catch {
      toast.error('เพิ่มไม่สำเร็จ');
    }
  };

  const handleRemove = async (exclusionId, customerId, displayName) => {
    try {
      await WebsiteFeeExclusionService.removeExclusion(website.id, customerId);
      setExclusions(prev => prev.filter(e => e.id !== exclusionId));
      toast.success(`ลบ ${displayName} สำเร็จ`);
    } catch {
      toast.error('ลบไม่สำเร็จ');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header row — click to expand */}
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-3">
          {isOpen
            ? <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
            : <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
          }
          <span className="font-medium text-gray-800">{website.webName}</span>
          {website.url && (
            <span className="text-xs text-gray-400 truncate max-w-xs">{website.url}</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          {website.websiteAdditionalRate > 0 && (
            <span className="text-orange-600 font-medium">Rate +{website.websiteAdditionalRate}</span>
          )}
          {website.websiteAdditionalTransactionFee > 0 && (
            <span className="text-orange-600 font-medium">Fee +{website.websiteAdditionalTransactionFee}฿</span>
          )}
          {loaded && (
            <span className="text-gray-400">{exclusions.length} คนที่ยกเว้น</span>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isOpen && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 space-y-4">
          {loading ? (
            <p className="text-sm text-gray-400">กำลังโหลด...</p>
          ) : (
            <>
              {/* ProfileCombo search to add */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">เพิ่มลูกค้าที่ยกเว้น</p>
                <ProfileCombo
                  key={comboKey}
                  profiles={availableProfiles}
                  onChange={handleSelect}
                />
              </div>

              {/* Excluded customer list */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  รายชื่อที่ยกเว้นอยู่ ({exclusions.length} คน)
                </p>
                {exclusions.length === 0 ? (
                  <p className="text-xs text-gray-400">ยังไม่มีลูกค้าที่ยกเว้น</p>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md bg-white text-sm">
                    {exclusions.map(e => (
                      <li
                        key={e.id}
                        className="flex justify-between items-center px-3 py-2"
                      >
                        <span>
                          <span className="font-medium">{e.customer?.contact}</span>
                          {e.customer?.smileName && (
                            <span className="text-gray-400 text-xs ml-1">({e.customer.smileName})</span>
                          )}
                        </span>
                        <button
                          onClick={() => handleRemove(e.id, e.customer?.id, e.customer?.contact || e.customer?.smileName)}
                          className="flex items-center gap-1 text-red-500 hover:text-red-700 text-xs"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                          ลบ
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const WebsiteFeeExclusionPage = () => {
  const { websites, refreshWebsites } = useManageWebsites();
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();

  useEffect(() => {
    refreshWebsites();
    getCustomerProfiles();
  }, []);

  const feeWebsites = websites.filter(
    w => Number(w.websiteAdditionalRate) > 0 || Number(w.websiteAdditionalTransactionFee) > 0
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <h2 className="text-4xl font-bold mb-2">Website Fee Exclusions</h2>
      <p className="text-sm text-gray-500 mb-6">
        จัดการลูกค้าที่ได้รับการยกเว้นค่าบริการพิเศษ — แสดงเฉพาะเว็บไซต์ที่มีการตั้งค่าค่าบริการเพิ่มเติม
      </p>

      {feeWebsites.length === 0 ? (
        <p className="text-gray-400 text-sm">
          ยังไม่มีเว็บไซต์ที่มีค่าบริการเพิ่มเติม — ตั้งค่า Add. Rate หรือ Add. Fee ใน Website Setting ก่อน
        </p>
      ) : (
        <div className="space-y-3">
          {feeWebsites.map(w => (
            <WebsiteRow key={w.id} website={w} profiles={profiles || []} />
          ))}
        </div>
      )}
    </div>
  );
};

export default WebsiteFeeExclusionPage;
